import { supabase } from '@/api/supabaseClient';
import { client } from '@/api/client';

// Internal staff messaging (channels + DMs). Bypasses the generic
// client.entities system on purpose: DM lookups need an OR filter
// across sender/recipient that the generic filter() (equality-only)
// can't express, and several queries here need joins/dedup logic the
// generic system doesn't support either.

// ---------- channels ----------
// Every channel is visible to every active staff member of the
// carrier -- RLS (channels_select) already allows that, and there's
// no per-user "joined" gate on top of it: a channel someone else
// creates should show up for the whole team immediately, not require
// each person to separately discover and join it.

export async function ensureGeneralChannel(carrierId, userId) {
  const { data: existing } = await supabase
    .from('channels')
    .select('*')
    .eq('carrier_id', carrierId)
    .eq('is_default', true)
    .limit(1)
    .maybeSingle();
  if (existing) return existing;
  const { data, error } = await supabase
    .from('channels')
    .insert({ carrier_id: carrierId, name: 'general', description: 'Company-wide channel', created_by: userId, is_default: true })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function listAllChannels(carrierId) {
  const { data, error } = await supabase
    .from('channels')
    .select('*')
    .eq('carrier_id', carrierId)
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createChannel(carrierId, userId, name, description) {
  const { data, error } = await supabase
    .from('channels')
    .insert({ carrier_id: carrierId, name: name.trim().toLowerCase().replace(/\s+/g, '-'), description: description || null, created_by: userId })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// ---------- staff directory (who can I message) ----------

export async function listStaffDirectory(excludeUserId) {
  const staff = await client.entities.StaffProfile.filter({ is_active: true }, 'full_name', 200);
  return staff.filter(s => s.user_id && s.user_id !== excludeUserId);
}

// ---------- messages ----------

export async function listChannelMessages(channelId) {
  const { data, error } = await supabase
    .from('staff_messages')
    .select('*')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return data || [];
}

export async function listDMMessages(carrierId, userId, otherUserId) {
  const { data, error } = await supabase
    .from('staff_messages')
    .select('*')
    .eq('carrier_id', carrierId)
    .or(`and(sender_id.eq.${userId},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${userId})`)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return data || [];
}

// Recent DM conversations, one row per counterpart (most recent
// message only) -- PostgREST has no GROUP BY, so this fetches recent
// DMs involving me and dedupes client-side by counterpart. Fine at
// internal-team-chat volume; would need a real view/RPC at scale.
export async function listRecentDMCounterparts(carrierId, userId) {
  const { data, error } = await supabase
    .from('staff_messages')
    .select('*')
    .eq('carrier_id', carrierId)
    .not('recipient_id', 'is', null)
    .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  const seen = new Map();
  for (const m of data || []) {
    const other = m.sender_id === userId ? m.recipient_id : m.sender_id;
    if (!seen.has(other)) seen.set(other, m);
  }
  return [...seen.entries()].map(([userId, lastMessage]) => ({ userId, lastMessage }));
}

export async function sendChannelMessage(carrierId, channelId, senderId, body) {
  const { data, error } = await supabase
    .from('staff_messages')
    .insert({ carrier_id: carrierId, channel_id: channelId, sender_id: senderId, body })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function sendDirectMessage(carrierId, senderId, recipientId, body) {
  const { data, error } = await supabase
    .from('staff_messages')
    .insert({ carrier_id: carrierId, recipient_id: recipientId, sender_id: senderId, body })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// ---------- realtime ----------

// Channel topics need to be unique PER SUBSCRIBER, not just per carrier --
// both Layout.jsx (sidebar unread badge) and TeamChat.jsx (its own
// message list) subscribe to staff_messages for the same carrier at the
// same time. Supabase's client keys channels by topic string; two
// `.channel('staff_messages-<carrierId>')` calls resolve to the SAME
// underlying channel object, so the second caller's `.on()` lands after
// the first caller's `.subscribe()` already fired, which throws
// ("cannot add postgres_changes callbacks... after subscribe()"). A
// random suffix per call site guarantees each subscriber gets its own
// channel regardless of how many places subscribe to the same table.
const uniqueChannelId = () => Math.random().toString(36).slice(2);

// Invalidate-and-refetch, same design as client.entities.X.subscribe()
// elsewhere in the app -- callback takes no payload, caller refetches.
export function subscribeStaffMessages(carrierId, cb) {
  const channel = supabase
    .channel(`staff_messages-${carrierId}-${uniqueChannelId()}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'staff_messages', filter: `carrier_id=eq.${carrierId}` }, () => cb())
    .subscribe();
  return () => supabase.removeChannel(channel);
}

export function subscribeChannels(carrierId, cb) {
  const channel = supabase
    .channel(`channels-${carrierId}-${uniqueChannelId()}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'channels', filter: `carrier_id=eq.${carrierId}` }, () => cb())
    .subscribe();
  return () => supabase.removeChannel(channel);
}

// ---------- presence (online/offline) ----------
// Ephemeral -- no table. Every open Team Chat tab tracks itself on one
// shared per-carrier presence channel; `sync` fires with the full set
// of currently-tracked keys whenever anyone joins/leaves, so the
// caller always gets the complete online set, not a diff to merge.
export function subscribePresence(carrierId, selfUserId, onChange) {
  const channel = supabase.channel(`presence-${carrierId}`, {
    config: { presence: { key: selfUserId } },
  });
  channel.on('presence', { event: 'sync' }, () => {
    onChange(new Set(Object.keys(channel.presenceState())));
  });
  channel.subscribe(async (status) => {
    if (status === 'SUBSCRIBED') await channel.track({ online_at: new Date().toISOString() });
  });
  return () => supabase.removeChannel(channel);
}

// ---------- reactions ----------

export async function listReactions(messageIds) {
  if (!messageIds?.length) return [];
  const { data, error } = await supabase
    .from('staff_message_reactions')
    .select('*')
    .in('message_id', messageIds);
  if (error) throw error;
  return data || [];
}

// Toggle: insert if this user hasn't reacted with this emoji on this
// message yet, delete if they have -- matches the tap-to-toggle
// convention every chat app uses for reactions.
export async function toggleReaction(carrierId, messageId, userId, emoji) {
  const { data: existing } = await supabase
    .from('staff_message_reactions')
    .select('id')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle();
  if (existing) {
    const { error } = await supabase.from('staff_message_reactions').delete().eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('staff_message_reactions')
      .insert({ carrier_id: carrierId, message_id: messageId, user_id: userId, emoji });
    if (error) throw error;
  }
}

export function subscribeReactions(carrierId, cb) {
  const channel = supabase
    .channel(`staff_message_reactions-${carrierId}-${uniqueChannelId()}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'staff_message_reactions', filter: `carrier_id=eq.${carrierId}` }, () => cb())
    .subscribe();
  return () => supabase.removeChannel(channel);
}

// ---------- read receipts ----------
// conversationKey is 'channel:<id>' for a channel or 'dm:<a>:<b>'
// (participant ids sorted) for a DM, so both sides of a DM land on
// the same key without nullable dual-FK columns.
export function channelConversationKey(channelId) {
  return `channel:${channelId}`;
}
export function dmConversationKey(userIdA, userIdB) {
  return `dm:${[userIdA, userIdB].sort().join(':')}`;
}

export async function markConversationRead(carrierId, userId, conversationKey) {
  const { error } = await supabase
    .from('staff_chat_reads')
    .upsert(
      { carrier_id: carrierId, user_id: userId, conversation_key: conversationKey, last_read_at: new Date().toISOString() },
      { onConflict: 'user_id,conversation_key' }
    );
  if (error) throw error;
}

export async function listConversationReads(conversationKey) {
  const { data, error } = await supabase
    .from('staff_chat_reads')
    .select('*')
    .eq('conversation_key', conversationKey);
  if (error) throw error;
  return data || [];
}

export function subscribeReads(carrierId, cb) {
  const channel = supabase
    .channel(`staff_chat_reads-${carrierId}-${uniqueChannelId()}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'staff_chat_reads', filter: `carrier_id=eq.${carrierId}` }, () => cb())
    .subscribe();
  return () => supabase.removeChannel(channel);
}

// ---------- avatars ----------
// One object per user at <carrier_id>/<user_id>/avatar.<ext>, upserted
// on re-upload (see 0031_staff_avatars_storage.sql). Returns a
// cache-busted public URL -- the object path never changes across
// re-uploads, so without the query param every viewer's browser would
// keep showing the old cached image after a change.
export async function uploadAvatar(carrierId, userId, file) {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
  const path = `${carrierId}/${userId}/avatar.${ext}`;
  const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, cacheControl: '3600' });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return `${data.publicUrl}?v=${Date.now()}`;
}

// Chat backgrounds are a purely cosmetic personal preference (see TeamChat.jsx's
// chatBgKey comment) -- they were previously going through
// client.integrations.Core.UploadFile(), which uploads to the PRIVATE
// 'documents' bucket and mints a signed URL. That's correct for actual PII
// documents (CDL, banking) but wrong here: signed-URL fetches to a private
// bucket aren't served off the same fast path as this public 'avatars'
// bucket, so the background visibly re-fetched slowly every time TeamChat
// remounted. Reuses the same bucket/path shape as uploadAvatar above.
export async function uploadChatBackground(carrierId, userId, file) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${carrierId}/${userId}/chat-bg.${ext}`;
  const { error } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, cacheControl: '3600' });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return `${data.publicUrl}?v=${Date.now()}`;
}

// ---------- unread count (sidebar nav badge) ----------

// Counts CONVERSATIONS with unread activity (channels + DMs), not raw
// message count -- matches the badge convention every chat app uses
// (Slack/Teams/Instagram: "3" means 3 threads have something new, not
// "3 messages"). A conversation is unread if its most recent message
// is newer than this user's own staff_chat_reads.last_read_at for that
// conversation (or has no read row at all yet), UNLESS that most recent
// message is one the user sent themselves -- nothing new for them to
// read in that case even if they haven't explicitly "opened" it since.
export async function countUnreadForUser(carrierId, userId) {
  const [{ data: messages, error: mErr }, { data: myReads, error: rErr }] = await Promise.all([
    supabase
      .from('staff_messages')
      .select('channel_id, sender_id, recipient_id, created_at')
      .eq('carrier_id', carrierId)
      .order('created_at', { ascending: false })
      .limit(500),
    supabase
      .from('staff_chat_reads')
      .select('conversation_key, last_read_at')
      .eq('user_id', userId),
  ]);
  if (mErr) throw mErr;
  if (rErr) throw rErr;

  const readMap = new Map((myReads || []).map(r => [r.conversation_key, r.last_read_at]));
  const latestByConv = new Map(); // conversation_key -> { createdAt, senderId }
  for (const m of messages || []) {
    let key;
    if (m.channel_id) {
      key = channelConversationKey(m.channel_id);
    } else if (m.sender_id === userId || m.recipient_id === userId) {
      key = dmConversationKey(userId, m.sender_id === userId ? m.recipient_id : m.sender_id);
    } else {
      continue; // someone else's DM
    }
    if (!latestByConv.has(key)) latestByConv.set(key, { createdAt: m.created_at, senderId: m.sender_id });
  }

  let unread = 0;
  for (const [key, { createdAt, senderId }] of latestByConv) {
    if (senderId === userId) continue;
    const lastRead = readMap.get(key);
    if (!lastRead || new Date(createdAt) > new Date(lastRead)) unread++;
  }
  return unread;
}
